import { isTextPart } from '@/components/assistant-ui/message-part-utils';
import { isUnchecked } from '@/lib/pii/unchecked-messages';

import type {
  AssistantClient,
  ExportedMessageRepositoryItem,
  ThreadMessage,
} from '@assistant-ui/react';
import type { UIMessage } from 'ai';

type ThreadClient = AssistantClient['thread'];

/**
 * Takes the refused message off the thread and returns its text, when it is still the thread's
 * newest: after a thread switch the head is another thread's message, which stays. Earlier messages
 * the server never answered go with it, along with their failed replies: the server checked them as
 * part of this turn, so any of them may be what it refused. Their texts come first, oldest first.
 * The thread is re-imported rather than the messages deleted: deleting goes through the history
 * adapter, which cannot delete. Runs inside the chat's onError, before the run settles, so history
 * never persists the refused message.
 */
export function dropRefusedMessage({
  thread,
  messageId,
}: {
  thread: ThreadClient;
  messageId: string | undefined;
}): string | null {
  // The export, not getState(): the thread state has not re-rendered since the send, so only the
  // repository already holds the refused message, as its head.
  const { headId, messages } = thread.export();
  if (headId !== messageId) {
    return null;
  }
  const byId = new Map(messages.map(item => [item.message.id, item]));
  const parentOf = (item: ExportedMessageRepositoryItem) =>
    item.parentId === null ? undefined : byId.get(item.parentId);
  const refused = messages.find(item => item.message.id === headId);
  if (refused?.message.role !== 'user') {
    return null;
  }

  /** The unchecked user message before `item`, with the failed reply a send that got no answer
   *  may have left under it; empty when the message before is checked. */
  const uncheckedTurnBefore = (item: ExportedMessageRepositoryItem) => {
    const parent = parentOf(item);
    const previous = parent?.message.role === 'assistant' ? parentOf(parent) : parent;
    if (!parent || previous?.message.role !== 'user' || !isUnchecked(previous.message.id)) {
      return [];
    }
    return parent === previous ? [previous] : [previous, parent];
  };

  const dropped = [refused];
  let turn = uncheckedTurnBefore(refused);
  while (turn.length > 0) {
    dropped.unshift(...turn);
    turn = uncheckedTurnBefore(dropped[0]);
  }

  const droppedIds = new Set(dropped.map(item => item.message.id));
  const isDropped = (item: ExportedMessageRepositoryItem): boolean => {
    const parent = parentOf(item);
    return droppedIds.has(item.message.id) || (parent !== undefined && isDropped(parent));
  };
  thread.import({
    headId: dropped[0].parentId,
    messages: messages.filter(item => !isDropped(item)),
  });

  return dropped
    .filter(item => item.message.role === 'user')
    .map(item => textOf(item.message))
    .filter(Boolean)
    .join('\n');
}

function textOf(message: ThreadMessage): string {
  return message.content
    .filter(isTextPart)
    .map(part => part.text)
    .join('');
}

/** The chat's messages with the masked message's text replaced by what the server saved; unchanged
 *  when the chat does not hold that message. */
export function withMaskedText({
  messages,
  messageId,
  text,
}: {
  messages: UIMessage[];
  messageId: string | undefined;
  text: string;
}): UIMessage[] {
  if (!messages.some(message => message.id === messageId)) {
    return messages;
  }
  return messages.map(message =>
    message.id === messageId
      ? {
          ...message,
          parts: [{ type: 'text', text }, ...message.parts.filter(part => part.type !== 'text')],
        }
      : message,
  );
}
